import { glassFrost, hardShadowColor } from "@keepoinc/site-page-react-component";
import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { ButtonStyle, Theme } from "../../../../../../model/theme";
import { classNames } from "../../../../../../utils/styles";
import { Divider } from "../../styles";
import { KindOptions, KindsContainer } from "../BackgroundKinds";

const VARIANTS = ["solid", "glass", "outline"] as const;
// pill first: the default (how buttons always looked)
const CORNERS = ["pill", "rounded", "square"] as const;
const SHADOWS = ["none", "soft", "hard"] as const;

/** The corners as the Keepo draws them (its buttons'), smaller in the cards. */
const RADIUS = { square: "2px", rounded: "8px", pill: "999px" } as const;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

/* A small button in each card, drawn as the Keepo does (over the page's color) */
const Sample = styled.span`
  padding: 0.375rem 0.625rem;
  font-family: Poppins, sans-serif;
  font-size: 0.5625rem;
  font-weight: 600;
  white-space: nowrap;
`;

type Props = {
  /** None: solid pills (how buttons always looked). */
  value?: ButtonStyle;
  onChange: (style: ButtonStyle) => void;
  /** The Keepo's colors: the cards draw its buttons with them. */
  theme: Theme;
  /** PLUS only: any choice asks to subscribe (`onLocked`) instead. */
  locked?: boolean;
  onLocked?: () => void;
};

/**
 * The buttons' look (link cards keep their own), each choice a card with a button drawn in the Keepo's colors (as the
 * background's styles): solid, glass or outline; square, rounded or pill corners; no, soft or hard shadow.
 */
export const ButtonStyleFields = ({ value, onChange, theme, locked, onLocked }: Props) => {
  const { t } = useTranslation();
  const prefix = "pages.editor.settings.groups.buttonStyle";
  const variant = value?.variant ?? "solid";
  const corners = value?.corners ?? "pill";
  const shadow = value?.shadow ?? "none";
  const choose = (style: Partial<Omit<ButtonStyle, "shadow">> & { shadow?: (typeof SHADOWS)[number] }) => {
    if (locked) {
      onLocked?.();
      return;
    }
    const next = { variant, corners, shadow, ...style };
    onChange({
      variant: next.variant,
      corners: next.corners,
      ...(next.shadow !== "none" && { shadow: next.shadow as ButtonStyle["shadow"] }),
    });
  };

  // a button as the Keepo draws it, with the choice being shown
  const sample = (look: { variant?: string; corners?: string; shadow?: string }) => {
    const v = look.variant ?? variant;
    const c = (look.corners ?? corners) as keyof typeof RADIUS;
    const s = look.shadow ?? shadow;
    const style: CSSProperties = {
      borderRadius: RADIUS[c],
      ...(v === "outline"
        ? { background: "transparent", color: theme.buttons, border: `2px solid ${theme.buttons}` }
        : v === "glass"
          ? // frosted white over the page, in the page's text color (as the Keepo draws glass)
            {
              background: glassFrost(theme.primary) ? "rgba(0, 0, 0, 0.06)" : "rgba(255, 255, 255, 0.25)",
              border: `1px solid ${glassFrost(theme.primary) ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.4)"}`,
              color: theme.secondary,
            }
          : { background: theme.buttons, color: theme.textButtons }),
      ...(s === "soft" && { boxShadow: "0 4px 8px rgba(0, 0, 0, 0.22)" }),
      ...(s === "hard" && { boxShadow: `3px 3px 0 ${hardShadowColor(theme.buttons, theme.primary)}` }),
    };
    return <Sample style={style}>{t(`${prefix}.sample`)}</Sample>;
  };

  // one row of cards: `group` names its labels (variants, cornerOptions, shadows)
  const row = <T extends string>(
    label: string,
    group: string,
    keys: readonly T[],
    selected: T,
    pick: (key: T) => void,
    look: (key: T) => object
  ) => (
    <KindsContainer>
      <span className="label" aria-hidden="true">
        {label}
      </span>
      <KindOptions role="radiogroup" aria-label={label}>
        {keys.map(key => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={selected === key}
            className={classNames({ selected: selected === key })}
            onClick={() => pick(key)}>
            <span className="frame" aria-hidden="true">
              <span className="thumbnail" style={{ backgroundColor: theme.primary }}>
                {sample(look(key))}
              </span>
            </span>
            {t(`${prefix}.${group}.${key}`)}
          </button>
        ))}
      </KindOptions>
    </KindsContainer>
  );

  return (
    <Fields>
      {row(
        t(`${prefix}.variant`),
        "variants",
        VARIANTS,
        variant,
        key => choose({ variant: key }),
        key => ({ variant: key })
      )}
      <Divider />
      {row(
        t(`${prefix}.corners`),
        "cornerOptions",
        CORNERS,
        corners,
        key => choose({ corners: key }),
        key => ({ corners: key })
      )}
      <Divider />
      {row(
        t(`${prefix}.shadow`),
        "shadows",
        SHADOWS,
        shadow,
        key => choose({ shadow: key }),
        key => ({ shadow: key })
      )}
    </Fields>
  );
};
